import {
  Component,
  inject,
  signal,
  computed,
  DestroyRef,
  effect,
} from '@angular/core';
import { SubscriptionService } from '../../services/subscription.service';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslocoDirective } from '@jsverse/transloco';
import { MatIcon } from '@angular/material/icon';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { PrimaryButtonComponent } from '../../../../../shared/components/primary-button/primary-button.component';
import { QuantityButtonComponent } from '../../../../../shared/components/quantity-button/quantity-button.component';
import { DialogModel } from '../../../../../shared/components/dialog/dialog';
import { SnackbarService } from '../../../../../core/services/snackbar.service';
import { CustomPortalError } from '../../../../../core/errors/models/CustomPortalError';
import { SubscriptionDialogService } from '../../services/subscription-dialog.service';
import { LicenseCalculationsService } from '../../services/license-calculations.service';
import { CurrentUserState } from '../../../../../core/store/current-user/current-user.state';
import { Store } from '@ngxs/store';
import {
  InvoicePreviewDTO,
  SubscriptionBillingResponseDTO,
} from '../../models/SubscriptionDTO';
import { AnalyticsService } from '../../../../../core/services/analytics/analytics.service';
import { debouncedSignal } from '../../../../../core/signal-utils/debouncedSignal';
import { SecondaryButtonComponent } from '../../../../../shared/components/secondary-button/secondary-button.component';

@Component({
  selector: 'app-subscription-settings',
  imports: [
    TranslocoDirective,
    MatIcon,
    CurrencyPipe,
    PrimaryButtonComponent,
    QuantityButtonComponent,
    DatePipe,
    SecondaryButtonComponent,
  ],
  templateUrl: './subscription-settings.component.html',
})
export class SubscriptionSettingsComponent {
  private readonly subscriptionService = inject(SubscriptionService);
  private readonly licenseCalculationService = inject(
    LicenseCalculationsService,
  );
  private readonly snackBarService = inject(SnackbarService);
  private readonly subscriptionDialogService = inject(
    SubscriptionDialogService,
  );
  private readonly analyticsService = inject(AnalyticsService);
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  private confirmUpgradeDialog: DialogModel | null = null;

  private readonly resolvedBilling = this.route.snapshot.data[
    'billingInformation'
  ] as SubscriptionBillingResponseDTO;

  private readonly initialQuantity = signal(
    this.resolvedBilling.upcomingInvoice.quantity,
  );
  readonly subscription = toSignal(
    this.store.select(CurrentUserState.subscription),
  );
  readonly billing = signal(this.resolvedBilling);
  readonly priceOfUpgrade = signal<InvoicePreviewDTO | null>(null);
  readonly newPlan = signal<InvoicePreviewDTO | null>(null);
  readonly quantity = signal(this.resolvedBilling.upcomingInvoice.quantity);
  readonly quantityOfUpgrade = signal(0);
  readonly enableFutureInvoiceView = signal(
    this.resolvedBilling.upcomingInvoice.quantity ===
      this.store.selectSnapshot(CurrentUserState.numberOfLicences),
  );
  readonly isLoading = signal(false);

  private readonly debouncedQuantity = debouncedSignal(this.quantity);

  readonly isAtMinLicenseCount = computed(
    () => this.quantity() <= this.billing().upcomingInvoice.quantity,
  );

  readonly nextYearTotalPriceWithoutTaxAndDiscount = computed(() => {
    const newPlan = this.newPlan();
    if (!newPlan) {
      return 0;
    }
    return newPlan.unitAmountExcludingTax * this.quantity();
  });

  readonly totalUpgradePriceWithoutTaxAndDiscount = computed(() => {
    const priceOfUpgrade = this.priceOfUpgrade();
    if (!priceOfUpgrade) {
      return 0;
    }
    return priceOfUpgrade.unitAmountExcludingTax * this.quantityOfUpgrade();
  });

  constructor() {
    effect(() => {
      const quantity = this.debouncedQuantity();
      if (quantity === this.initialQuantity()) {
        return;
      }
      this.isLoading.set(true);
      this.getUpgradePriceInformation(quantity);
    });
  }

  onStripeButtonClicked() {
    window.open(
      'https://billing.stripe.com/p/login/5kA4jTeSU4TraHefYY',
      '_blank',
    );
  }

  onQuantityChange(newQuantity: number) {
    this.quantity.set(newQuantity);
  }

  priceInEuros(cents: number): number {
    return cents / 100;
  }

  showConfirmUpgradeDialog(quantity: number) {
    const priceOfUpgrade = this.priceOfUpgrade();
    const newPlan = this.newPlan();
    if (!priceOfUpgrade || !newPlan) {
      return;
    }

    this.confirmUpgradeDialog =
      this.subscriptionDialogService.showConfirmationDialog({
        quantity,
        immediatePayment: priceOfUpgrade.total,
        yearlyPayment: newPlan.total,
        isFreeTrial: this.subscription()?.status === 'trialing',
        onConfirm: () => {
          this.upgradeSubscription(quantity);
        },
      });
  }

  private upgradeSubscription(quantity: number) {
    const dialog = this.confirmUpgradeDialog;
    if (dialog?.buttonRowData) {
      dialog.buttonRowData.confirmButtonLoading = true;
      dialog.buttonRowData.confirmButtonDisabled = true;
    }

    this.subscriptionService
      .upgradeSubscription(quantity)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.refreshBillingInformation();
          this.confirmUpgradeDialog?.closeDialog();
          this.licenseCalculationService.refreshLicenses();
          this.analyticsService.track('license_quantity_updated', {
            newQuantity: quantity,
            numberOfAddedLicenses: this.quantityOfUpgrade(),
            discount: this.priceInEuros(this.newPlan()?.discountAmount ?? 0),
            price: this.priceInEuros(this.newPlan()?.total ?? 0),
          });
        },
        error: (error: CustomPortalError) => {
          this.snackBarService.error(error.errorType);
        },
      });
  }

  private refreshBillingInformation() {
    this.subscriptionService
      .getBillingInformation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((billingDTO) => {
        this.billing.set(billingDTO);
        this.quantity.set(billingDTO.upcomingInvoice.quantity);
        this.initialQuantity.set(billingDTO.upcomingInvoice.quantity);
        const currentQuantity = this.store.selectSnapshot(
          CurrentUserState.numberOfLicences,
        );
        this.enableFutureInvoiceView.set(
          billingDTO.upcomingInvoice.quantity === currentQuantity,
        );
      });
  }

  private getUpgradePriceInformation(quantity: number) {
    this.subscriptionService
      .getUpgradePriceInformation(quantity)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((upgradePreviewResponseDTO) => {
        this.priceOfUpgrade.set(upgradePreviewResponseDTO.priceOfUpgrade);
        this.newPlan.set(upgradePreviewResponseDTO.newPlan);
        this.quantityOfUpgrade.set(
          quantity - this.billing().upcomingInvoice.quantity,
        );
        this.isLoading.set(false);
      });
  }
}
